import { useEffect, useMemo, useState } from "react";
import { formatDisplayName } from "../utils/displayName";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  AlertTriangle,
  ArrowLeft,
  CalendarClock,
  CheckCircle2,
  ClipboardList,
  MapPin,
  Pencil,
  Save,
  Trash2,
  UserRound,
  X,
} from "lucide-react";
import BrokerDashboardLayout from "../components/BrokerDashboardLayout";
import PortalFeedback from "../components/PortalFeedback";
import {
  contactPreferenceOptions,
  contactRoleOptions,
  PortalSelect,
  PortalServicesDropdown,
  priorityOptions,
} from "../components/PortalDropdown";
import { supabase } from "../supabaseClient";
import { formatExpectedTiming } from "../utils/timeFormat";
import { isTimeoutError, withTimeout } from "../utils/asyncTimeout";
import { normalizeProjectStatus } from "../utils/projectStatus";

const PROJECT_DETAIL_TIMEOUT_MS = 25000;

const PANEL =
  "rounded-2xl border border-white/15 bg-white/70 backdrop-blur-md shadow-[0_10px_30px_rgba(0,0,0,0.08)]";

const normalizeStatus = (status) => normalizeProjectStatus(status);

const splitServices = (value = "") =>
  value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

const getContactPreferenceLabel = (value) =>
  contactPreferenceOptions.find((option) => option.value === value)?.label || value || "—";
const formatHistoryExpectedTiming = (item, timeZone) => {
  const oldTiming = formatExpectedTiming(item.old_expected_datetime, timeZone);
  const newTiming = formatExpectedTiming(item.new_expected_datetime, timeZone);

  if (oldTiming === "—" && newTiming === "—") return "";
  if (oldTiming === newTiming || oldTiming === "—") return newTiming;
  if (newTiming === "—") return `Cleared from ${oldTiming}`;
  return `${oldTiming} → ${newTiming}`;
};
const formatStatusTransition = (item) => {
  if (!item.old_status || !item.new_status) return "Project details updated";
  return `${normalizeStatus(item.old_status)} → ${normalizeStatus(item.new_status)}`;
};
const buildFormData = (data) => ({
  priority: data.broker_intake?.priority || data.priority || "",
  services: splitServices(data.broker_intake?.services || ""),
  contact_name: data.broker_intake?.contact_name || "",
  contact_role: data.broker_intake?.contact_role || "",
  contact_phone: data.broker_intake?.contact_phone || "",
  contact_email: data.broker_intake?.contact_email || "",
  contact_method_preference: data.broker_intake?.contact_method_preference || "no preference",
  airport_identifier: data.broker_intake?.airport_identifier || "",
  notes: data.broker_intake?.notes || "",
});

const formatDateTime = (value) => {
  if (!value) return "—";
  return new Date(value).toLocaleString([], {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

export default function BrokerProjectDetailPage() {
  const { tail } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [project, setProject] = useState(null);
  const [history, setHistory] = useState([]);
  const [formData, setFormData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingSection, setSavingSection] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [editingRequest, setEditingRequest] = useState(false);
  const [editingContact, setEditingContact] = useState(false);
  const [errorText, setErrorText] = useState("");
  const [successText, setSuccessText] = useState("");
  const [toast, setToast] = useState(null);

  const tailParam = decodeURIComponent(tail || "").toUpperCase();
  const listPath = location.state?.from || "/brokerprojects";

  const loadProject = async () => {
    setLoading(true);
    setErrorText("");
    setSuccessText("");

    try {
      const { data, error } = await withTimeout(
        supabase
          .from("project_status")
          .select(
            `
              id,
              intake_id,
              tail_number,
              current_status,
              priority,
              assigned_pilot,
              expected_datetime,
              timezone,
              notes,
              created_at,
              updated_at,
              broker_intake (
                id,
                created_at,
                services,
                tail_number,
                airport_identifier,
                contact_name,
                contact_phone,
                contact_email,
                contact_method_preference,
                contact_role,
                priority,
                notes
              )
            `
          )
          .ilike("tail_number", tailParam)
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle(),
        PROJECT_DETAIL_TIMEOUT_MS,
        "Project detail lookup timed out"
      );

      if (error) {
        setErrorText(error.message || "Could not load project.");
        setLoading(false);
        return;
      }

      if (!data) {
        setErrorText("Project not found.");
        setLoading(false);
        return;
      }

      setProject(data);
      setFormData(buildFormData(data));

      const { data: historyRows } = await withTimeout(
        supabase
          .from("project_status_history")
          .select("id, old_status, new_status, old_priority, new_priority, old_assigned_pilot, new_assigned_pilot, old_expected_datetime, new_expected_datetime, old_notes, new_notes, created_at")
          .eq("project_status_id", data.id)
          .order("created_at", { ascending: false })
          .limit(8),
        PROJECT_DETAIL_TIMEOUT_MS,
        "Project history lookup timed out"
      );

      setHistory(historyRows || []);
    } catch (error) {
      setErrorText(
        isTimeoutError(error)
          ? "Project took too long to load. Refresh the page or sign out and back in."
          : "Could not load project."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProject();
  }, [tailParam]);

  const isLocked = useMemo(() => {
    if (!project?.expected_datetime) return false;
    return new Date(project.expected_datetime).getTime() - Date.now() <= 24 * 60 * 60 * 1000;
  }, [project?.expected_datetime]);

  const status = normalizeStatus(project?.current_status);
  const intake = project?.broker_intake;
  const canEdit = !!project && !isLocked;
  const savingRequest = savingSection === "request";
  const savingContact = savingSection === "contact";

  const resetFormData = () => {
    if (project) setFormData(buildFormData(project));
    setErrorText("");
    setSuccessText("");
  };

  const toggleService = (service) => {
    setFormData((prev) => {
      const exists = prev.services.includes(service);
      return {
        ...prev,
        services: exists
          ? prev.services.filter((item) => item !== service)
          : [...prev.services, service],
      };
    });
  };

  const handleSaveRequest = async () => {
    if (!canEdit || !intake?.id) return;
    if (!formData.priority) {
      setErrorText("Select a priority before saving.");
      return;
    }
    if (formData.services.length === 0) {
      setErrorText("Select at least one service before saving.");
      return;
    }

    setSavingSection("request");
    setErrorText("");
    setSuccessText("");

    const { error } = await withTimeout(
      supabase
        .from("broker_intake")
        .update({
          priority: formData.priority,
          services: formData.services.join(", "),
          airport_identifier: formData.airport_identifier.trim().toUpperCase() || null,
        })
        .eq("id", intake.id),
      PROJECT_DETAIL_TIMEOUT_MS,
      "Request details save timed out"
    ).catch((error) => ({ error }));

    if (error) {
      setSavingSection("");
      setErrorText(
        isTimeoutError(error)
          ? "Request details took too long to save. Refresh and try again."
          : error.message || "Could not save request details."
      );
      return;
    }

    setSavingSection("");
    setEditingRequest(false);
    const savedMessage = "Request details saved. Strobe Aviation will be able to review the update.";
    setSuccessText(savedMessage);
    setToast({
      title: "Saved",
      message: savedMessage,
    });
    window.setTimeout(() => setToast(null), 5000);
    await loadProject();
  };

  const handleSaveContact = async () => {
    if (!canEdit || !intake?.id) return;
    if (!formData.contact_name.trim()) {
      setErrorText("Enter the point of contact name.");
      return;
    }
    if (!formData.contact_role) {
      setErrorText("Select the point of contact role.");
      return;
    }
    if (!formData.contact_phone.trim() && !formData.contact_email.trim()) {
      setErrorText("Enter a point of contact phone or email.");
      return;
    }

    setSavingSection("contact");
    setErrorText("");
    setSuccessText("");

    const { error } = await withTimeout(
      supabase
        .from("broker_intake")
        .update({
          contact_name: formData.contact_name.trim(),
          contact_role: formData.contact_role,
          contact_phone: formData.contact_phone.trim() || null,
          contact_email: formData.contact_email.trim() || null,
          contact_method_preference: formData.contact_method_preference,
          notes: formData.notes.trim() || null,
        })
        .eq("id", intake.id),
      PROJECT_DETAIL_TIMEOUT_MS,
      "Contact save timed out"
    ).catch((error) => ({ error }));

    if (error) {
      setSavingSection("");
      setErrorText(
        isTimeoutError(error)
          ? "Point of contact changes took too long to save. Refresh and try again."
          : error.message || "Could not save point of contact changes."
      );
      return;
    }

    setSavingSection("");
    setEditingContact(false);
    const savedMessage = "Point of contact changes saved. Strobe Aviation will be able to review the update.";
    setSuccessText(savedMessage);
    setToast({
      title: "Saved",
      message: savedMessage,
    });
    window.setTimeout(() => setToast(null), 5000);
    await loadProject();
  };

  const handleDelete = async () => {
    if (!canEdit || !intake?.id) return;
    const confirmed = window.confirm(`Delete project request for ${project.tail_number}? This cannot be undone.`);
    if (!confirmed) return;

    setDeleting(true);
    setErrorText("");
    setSuccessText("");

    const { error } = await withTimeout(
      supabase
        .from("broker_intake")
        .delete()
        .eq("id", intake.id),
      PROJECT_DETAIL_TIMEOUT_MS,
      "Project delete timed out"
    ).catch((error) => ({ error }));

    if (error) {
      setDeleting(false);
      setErrorText(
        isTimeoutError(error)
          ? "Delete took too long. Refresh and check Projects before trying again."
          : error.message || "Could not delete project."
      );
      return;
    }

    setDeleting(false);
    navigate(listPath, {
      state: {
        title: "Deleted",
        message: `${project?.tail_number || "Project"} was deleted.`,
      },
    });
  };

  const Field = ({ label, value }) => (
    <div className="rounded-xl border border-black/10 bg-white/70 p-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-black/45">{label}</p>
      <p className="mt-1 text-sm font-medium text-[#1A1A1A]">{value || "—"}</p>
    </div>
  );

  return (
    <BrokerDashboardLayout>
      <div className="space-y-6 text-[#1A1A1A]">
        {toast && (
          <div className="fixed right-4 top-4 z-50 w-[min(92vw,360px)] rounded-xl border border-black/10 bg-white p-4 shadow-2xl">
            <button
              type="button"
              onClick={() => setToast(null)}
              className="absolute right-2 top-2 rounded p-1 text-black/50 hover:bg-black/5 hover:text-black"
              aria-label="Dismiss saved message"
            >
              <X className="h-4 w-4" />
            </button>
            <div className="flex gap-3 pr-5">
              <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-[#EB040D]" />
              <div>
                <p className="text-sm font-semibold text-[#1A1A1A]">{toast.title}</p>
                <p className="mt-1 text-sm text-black/65">{toast.message}</p>
              </div>
            </div>
          </div>
        )}

        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <Link to={listPath} className="mb-2 inline-flex items-center gap-2 text-sm text-black/55 hover:text-black">
              <ArrowLeft className="h-4 w-4" />
              Projects
            </Link>
            <h1 className="text-2xl font-extrabold tracking-tight">Project Detail</h1>
            <p className="text-xs text-black/60 mt-1">Tail Number: {project?.tail_number || tailParam}</p>
          </div>

          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
            <button
              type="button"
              onClick={handleDelete}
              disabled={!canEdit || deleting || loading}
              className="inline-flex h-10 items-center justify-center gap-2 rounded-md border border-red-200 bg-white px-4 text-sm font-semibold text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Trash2 className="h-4 w-4" />
              {deleting ? "Deleting..." : "Delete"}
            </button>
          </div>
        </div>

        {loading ? (
          <div className={`${PANEL} p-6 text-sm text-black/60`}>Loading project…</div>
	        ) : errorText && !project ? (
	          <PortalFeedback type="error">{errorText}</PortalFeedback>
	        ) : (
          <>
            {isLocked && (
              <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                <div className="flex gap-2">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  <p>
                    Service is scheduled within 24 hours. Please contact help@strobeaviation.com and someone will reach out to you.
                  </p>
                </div>
              </div>
            )}

	            {(errorText || successText) && (
	              <PortalFeedback type={errorText ? "error" : "success"}>
	                {errorText || successText}
	              </PortalFeedback>
	            )}

            <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1.1fr)_minmax(360px,0.9fr)]">
              <div className="space-y-6">
                <div className={`${PANEL} p-5`}>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <Field label="Status" value={status} />
                    <Field label="Expected By" value={formatExpectedTiming(project.expected_datetime, project.timezone)} />
                    <Field label="Assigned To" value={formatDisplayName(project.assigned_pilot) || "Unassigned"} />
                    <Field label="Requested" value={formatDateTime(intake?.created_at)} />
                  </div>
                </div>

                <div className={`${PANEL} overflow-hidden`}>
                  <div className="border-b border-black/10 px-5 py-4">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <ClipboardList className="h-5 w-5 text-[#EB040D]" />
                          <h2 className="text-lg font-bold tracking-tight">Request Details</h2>
                        </div>
                        <p className="mt-1 text-sm text-black/60">
                          Broker-editable request information. Status, schedule, and assignment are managed by Strobe.
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          if (editingRequest) resetFormData();
                          setEditingRequest((current) => !current);
                        }}
                        disabled={!canEdit || loading || savingRequest}
                        className="inline-flex h-10 items-center justify-center gap-2 rounded-md border border-black/10 bg-white px-4 text-sm font-semibold hover:bg-black/[0.03] disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {editingRequest ? <X className="h-4 w-4" /> : <Pencil className="h-4 w-4" />}
                        {editingRequest ? "Cancel" : "Modify"}
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-6 px-5 py-5 lg:grid-cols-2 xl:grid-cols-1 2xl:grid-cols-2">
                    <div>
                      <label className="block text-sm font-semibold mb-1">Priority</label>
                      {editingRequest ? (
                        <PortalSelect
                          value={formData.priority}
                          placeholder="Select Priority"
                          options={priorityOptions}
                          onChange={(value) => setFormData({ ...formData, priority: value })}
                        />
                      ) : (
                        <p className="rounded-md border border-black/10 bg-white/70 px-3 py-2 text-sm">{intake?.priority || project.priority || "—"}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-semibold mb-1">ICAO Location</label>
                      {editingRequest ? (
                        <input
                          type="text"
                          value={formData.airport_identifier}
                          onChange={(e) => setFormData({ ...formData, airport_identifier: e.target.value })}
                          className="w-full rounded-md border border-black/10 px-3 py-2 text-sm"
                        />
                      ) : (
                        <p className="rounded-md border border-black/10 bg-white/70 px-3 py-2 text-sm">{intake?.airport_identifier || "—"}</p>
                      )}
                    </div>

                    <div className="lg:col-span-2 xl:col-span-1 2xl:col-span-2">
                      <label className="block text-sm font-semibold mb-2">Services</label>
                      {editingRequest ? (
                        <PortalServicesDropdown
                          selectedServices={formData.services}
                          onToggle={toggleService}
                        />
                      ) : (
                        <p className="rounded-md border border-black/10 bg-white/70 px-3 py-2 text-sm leading-relaxed">{intake?.services || "—"}</p>
                      )}
                    </div>
                  </div>

                  {editingRequest && (
                    <div className="flex justify-end border-t border-black/10 px-5 py-4">
                      <button
                        type="button"
                        onClick={handleSaveRequest}
                        disabled={savingRequest}
                        className="inline-flex h-10 items-center justify-center gap-2 rounded-md bg-[#EB040D] px-5 text-sm font-semibold text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        <Save className="h-4 w-4" />
                        {savingRequest ? "Saving..." : "Save Request Details"}
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-6">
                <div className={`${PANEL} overflow-hidden`}>
              <div className="border-b border-black/10 px-5 py-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <UserRound className="h-5 w-5 text-[#EB040D]" />
                    <h2 className="text-lg font-bold tracking-tight">Point of Contact</h2>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      if (editingContact) resetFormData();
                      setEditingContact((current) => !current);
                    }}
                    disabled={!canEdit || loading || savingContact}
                    className="inline-flex h-10 items-center justify-center gap-2 rounded-md border border-black/10 bg-white px-4 text-sm font-semibold hover:bg-black/[0.03] disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {editingContact ? <X className="h-4 w-4" /> : <Pencil className="h-4 w-4" />}
                    {editingContact ? "Cancel" : "Modify"}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-6 px-5 py-5 lg:grid-cols-2">
                <div>
                  <label className="block text-sm font-semibold mb-1">Contact Name</label>
                  {editingContact ? (
                    <input
                      type="text"
                      value={formData.contact_name}
                      onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                      className="w-full rounded-md border border-black/10 px-3 py-2 text-sm"
                    />
                  ) : (
                    <p className="rounded-md border border-black/10 bg-white/70 px-3 py-2 text-sm">{formatDisplayName(intake?.contact_name) || "—"}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-1">Contact Role</label>
                  {editingContact ? (
                    <PortalSelect
                      value={formData.contact_role}
                      placeholder="Select role"
                      options={contactRoleOptions}
                      onChange={(value) => setFormData({ ...formData, contact_role: value })}
                    />
                  ) : (
                    <p className="rounded-md border border-black/10 bg-white/70 px-3 py-2 text-sm">{intake?.contact_role || "—"}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-1">Phone</label>
                  {editingContact ? (
                    <input
                      type="tel"
                      value={formData.contact_phone}
                      onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                      className="w-full rounded-md border border-black/10 px-3 py-2 text-sm"
                    />
                  ) : (
                    <p className="rounded-md border border-black/10 bg-white/70 px-3 py-2 text-sm">{intake?.contact_phone || "—"}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-1">Email</label>
                  {editingContact ? (
                    <input
                      type="email"
                      value={formData.contact_email}
                      onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                      className="w-full rounded-md border border-black/10 px-3 py-2 text-sm"
                    />
                  ) : (
                    <p className="rounded-md border border-black/10 bg-white/70 px-3 py-2 text-sm">{intake?.contact_email || "—"}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-1">Contact Preference</label>
                  {editingContact ? (
                    <PortalSelect
                      value={formData.contact_method_preference}
                      placeholder="No preference"
                      options={contactPreferenceOptions}
                      onChange={(value) => setFormData({ ...formData, contact_method_preference: value })}
                    />
                  ) : (
                    <p className="rounded-md border border-black/10 bg-white/70 px-3 py-2 text-sm">
                      {getContactPreferenceLabel(intake?.contact_method_preference)}
                    </p>
                  )}
                </div>

                <div className="lg:col-span-2">
                  <label className="block text-sm font-semibold mb-1">Notes</label>
                  {editingContact ? (
                    <textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      rows={4}
                      className="w-full rounded-md border border-black/10 px-3 py-2 text-sm"
                    />
                  ) : (
                    <p className="min-h-[72px] rounded-md border border-black/10 bg-white/70 px-3 py-2 text-sm leading-relaxed">{intake?.notes || "—"}</p>
                  )}
                </div>
              </div>

              {editingContact && (
                <div className="flex justify-end border-t border-black/10 px-5 py-4">
                  <button
                    type="button"
                    onClick={handleSaveContact}
                    disabled={savingContact}
                    className="inline-flex h-10 items-center justify-center gap-2 rounded-md bg-[#EB040D] px-5 text-sm font-semibold text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <Save className="h-4 w-4" />
                    {savingContact ? "Saving..." : "Save Contact"}
                  </button>
                </div>
              )}
                </div>

                <div className={`${PANEL} overflow-hidden`}>
              <div className="border-b border-black/10 px-5 py-4">
                <div className="flex items-center gap-2">
                  <CalendarClock className="h-5 w-5 text-[#EB040D]" />
                  <h2 className="text-lg font-bold tracking-tight">Timeline</h2>
                </div>
              </div>

              <div className="px-5 py-5">
                {history.length === 0 ? (
                  <p className="text-sm text-black/55">No status history yet.</p>
                ) : (
                  <div className="space-y-3">
                    {history.map((item) => (
                      <div key={item.id} className="flex gap-3 rounded-xl border border-black/10 bg-white/70 p-3">
                        <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-[#EB040D]" />
                        <div>
                          <p className="text-sm font-semibold">
                            {formatStatusTransition(item)}
                          </p>
                          {formatHistoryExpectedTiming(item, project?.timezone) && (
                            <p className="text-xs font-medium text-black/70">
                              Expected: {formatHistoryExpectedTiming(item, project?.timezone)}
                            </p>
                          )}
                          <p className="text-xs text-black/50">{formatDateTime(item.created_at)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </BrokerDashboardLayout>
  );
}
